import React, { useMemo } from "react";
import { useList, HttpError, useCustomMutation, useTranslation, useGetIdentity } from "@refinedev/core";
import { useServicePricing } from "../../hooks/useServicePricing";
import { useServicePurchase } from "../../hooks/useServicePurchase";
import { FaArrowLeft, FaCheck, FaCalendar, FaCreditCard, FaWallet, FaCheckCircle } from "react-icons/fa";
import { createMachine, state, transition, action, invoke, reduce } from 'robot3';
import { useMachine } from 'react-robot';
import gql from "graphql-tag";
import { PAYMENT_METHODS, PaymentMethod } from "@components/atoms/paymentMethods";

interface PaymentMethodIconProps {
  paymentMethod: PaymentMethod;
  size?: 'sm' | 'md' | 'lg';
}

const PaymentMethodIcon = ({ paymentMethod, size = 'md' }: PaymentMethodIconProps) => {
  const sizeClasses = {
    sm: 'w-6 h-4',
    md: 'w-8 h-6',
    lg: 'w-10 h-8'
  };

  const imageSizeClasses = {
    sm: 'h-3 w-auto',
    md: 'h-4 w-auto',
    lg: 'h-5 w-auto'
  };

  return (
    <div className={`${sizeClasses[size]} ${paymentMethod.backgroundColor} rounded flex items-center justify-center`}>
      <img
        src={paymentMethod.imageUrl}
        alt={paymentMethod.name}
        className={imageSizeClasses[size]}
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
    </div>
  );
};

interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  duration_minutes: number;
  payment_link?: string;
}

interface ServicePopupProps {
  onBack?: () => void;
  className?: string;
  serviceType?: string; // e.g., "appointment", "consultation"
}

export const ServicePopup = ({
  onBack,
  className = "",
  serviceType = "appointment"
}: ServicePopupProps) => {
  const { translate } = useTranslation();
  const { data: identity } = useGetIdentity<{ id: string }>();
  const { basicPrice, premiumPriceBasic, premiumPricePro, isLoading: pricingLoading } = useServicePricing(serviceType as 'help' | 'consultation');

  // Obtener features y precios usando el hook
  const { data: features } = useList({
    resource: "features",
    filters: [{ field: "name", operator: "eq", value: serviceType }],
    pagination: { current: 1, pageSize: 1 },
    meta: { fields: ["id", "name"] }
  });

  const isLoading = pricingLoading || !features?.data;
  const featureId = features?.data?.[0]?.id;

  // Check if user has purchased this service
  const { hasPurchased, isLoading: purchaseLoading } = useServicePurchase(featureId);

  // Crear servicios basados en los precios obtenidos
  const getServices = (): Service[] => {
    if (!featureId) return [];

    const services: Service[] = [];

    // Servicio premium básico (PRO-A)
    if (premiumPriceBasic > 0) {
      services.push({
        id: featureId,
        name: serviceType === 'help' ? 'Servicio de Ayuda - PRO-A' : 'Consulta Profesional - PRO-A',
        description: 'Profesional certificado con experiencia especializada',
        price: Number(premiumPriceBasic),
        currency: 'COP',
        duration_minutes: serviceType === 'help' ? 45 : 60
      });
    }

    // Servicio premium pro (PRO-B)
    if (premiumPricePro > 0) {
      services.push({
        id: featureId,
        name: serviceType === 'help' ? 'Servicio de Ayuda - PRO-B' : 'Consulta Profesional - PRO-B',
        description: 'Profesional premium con atención prioritaria y especialización avanzada',
        price: Number(premiumPricePro),
        currency: 'COP',
        duration_minutes: serviceType === 'help' ? 45 : 60
      });
    }

    return services;
  };

  const displayServices = getServices();

  const { mutateAsync: createServicePayment } = useCustomMutation<{ create_service_payment: { redirect_link: string } }, HttpError, {}>()

  const createLink = async (ctx: { service: Service }) => {
    console.log('🔄 createLink called with:', ctx);
    console.log('🔄 featureId:', featureId);
    console.log('🔄 identity:', identity);

    // Validate inputs before making request
    if (!identity?.uid) {
      console.error('❌ No user identity found');
      throw new Error('No se pudo identificar al usuario. Por favor, inicia sesión nuevamente.');
    }

    if (!featureId && !ctx.service.id) {
      console.error('❌ No feature ID found');
      throw new Error('Error de configuración: ID de servicio no encontrado.');
    }

    if (!ctx.service.price || ctx.service.price <= 0) {
      console.error('❌ Invalid price:', ctx.service.price);
      throw new Error('Error de configuración: Precio inválido.');
    }

    try {
      const { data: response } = await createServicePayment({
        url: '',
        method: 'post',
        values: {},
        meta: {
          gqlMutation: gql`
            mutation CreateServicePayment($service_payment_request: ServicePaymentRequest!) {
              create_service_payment(service_payment_request: $service_payment_request) {
                redirect_link
              }
            }
          `,
          gqlVariables: {
            service_payment_request: {
              user_id: identity.uid,
              feature_id: featureId || ctx.service.id,
              feature_name: ctx.service.name,
              price: ctx.service.price,
              quantity: 1
            }
          }
        }
      })

      console.log('✅ Payment link created:', response);

      if (!response?.create_service_payment?.redirect_link) {
        throw new Error('No se recibió el enlace de pago del servidor.');
      }

      return response.create_service_payment.redirect_link
    } catch (error: any) {
      console.error('❌ Error creating payment link:', error);

      // Provide user-friendly error messages
      if (error.message?.includes('NO_USER_ID')) {
        throw new Error('Usuario no encontrado. Por favor, inicia sesión nuevamente.');
      } else if (error.message?.includes('FEATURE_NOT_FOUND')) {
        throw new Error('El servicio solicitado no está disponible.');
      } else if (error.message?.includes('INVALID_PRICE')) {
        throw new Error('Error en el precio del servicio. Contacta a soporte.');
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        throw new Error('Error de conexión. Verifica tu internet e intenta nuevamente.');
      } else {
        throw new Error(error.message || 'Error al crear el enlace de pago. Intenta nuevamente.');
      }
    }
  }

  const openLink = (ctx: any) => {
    console.log('🌐 Opening payment link:', ctx.payLink);
    window.open(ctx.payLink, '_blank');
  }

  const machine = useMemo(() => createMachine('service_selection', {
    service_selection: state(
      transition('SELECT_SERVICE', 'payment_method_selection', reduce((ctx: { service?: Service, paymentMethod?: string }, ev: { service: Service }) => ({ ...ctx, service: ev.service })))
    ),
    payment_method_selection: state(
      transition('SELECT_CARD_PAYMENT', 'loading_payment', reduce((ctx: { service?: Service, paymentMethod?: string }) => ({ ...ctx, paymentMethod: 'card' }))),
      transition('SELECT_OTHER_PAYMENT', 'loading_payment', reduce((ctx: { service?: Service, paymentMethod?: string }) => ({ ...ctx, paymentMethod: 'other' }))),
      transition('BACK_TO_SERVICE_SELECTION', 'service_selection')
    ),
    loading_payment: invoke(createLink,
      transition('done', 'waiting_confirmation', reduce((ctx: { service?: Service, paymentMethod?: string, payLink?: string }, ev: { data: string }) => ({ ...ctx, payLink: ev.data })), action(openLink)),
      transition('error', 'error')
    ),
    error: state(
      transition('RETRY', 'payment_method_selection'),
      transition('BACK_TO_SERVICE_SELECTION', 'service_selection')
    ),
    waiting_confirmation: state(
      transition('BACK_TO_SERVICE_SELECTION', 'service_selection')
    )
  }, () => ({ service: undefined as Service | undefined, paymentMethod: undefined as string | undefined })), [featureId])

  const [current, send] = useMachine(machine);

  console.log('🔍 Current state:', current.name);
  console.log('🔍 Current context:', current.context);
  if (isLoading || purchaseLoading) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${className}`}>
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  const renderServiceSelection = () => {
    // If user already purchased this service, show message
    if (hasPurchased) {
      return (
        <div className="p-8 text-center">
          <FaCheckCircle className="text-success text-4xl mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">Servicio ya adquirido</h3>
          <p className="text-base-content/70 mb-6">Ya tienes acceso a este servicio</p>
          {onBack && (
            <button onClick={onBack} className="btn btn-primary">
              Volver
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="p-4 md:p-8 bg-base-200">
        {/* Header Section */}
        <div className="text-center mb-8">
          {onBack && (
            <button onClick={onBack} className="btn btn-ghost btn-circle mb-4">
              <FaArrowLeft className="w-5 h-5" />
            </button>
          )}
          <h2 className="text-2xl font-bold text-base-content mb-2">
            {translate("page.services.title")}
          </h2>
          <p className="text-base-content/70">
            {translate("page.services.subtitle")}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {displayServices.map((service, index) => {
            const isPremium = service.name.includes('PRO-B');
            const isBasic = service.name.includes('PRO-A');
            const isPopular = isPremium;

            return (
              <div
                key={`${service.id}-${index}`}
                className={`relative card bg-base-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full ${isPopular ? 'border-2 border-primary' : ''
                  }`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-primary text-primary-content px-4 py-1 rounded-full text-sm font-semibold">
                      {translate("page.services.premium_badge")}
                    </div>
                  </div>
                )}

                <div className="card-body p-6 flex flex-col h-full">
                  {/* Service Header */}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold mb-2 text-base-content">
                      {service.name}
                    </h3>
                    <div className="h-20 flex items-center justify-center">
                      <p className="text-base-content/70 text-sm leading-relaxed line-clamp-4">
                        {service.description}
                      </p>
                    </div>
                  </div>

                  {/* Price Section */}
                  <div className="text-center mb-6">
                    <div className="rounded-xl p-6">
                      <div className="text-4xl font-bold text-secondary mb-1">
                        ${service.price.toLocaleString()}
                      </div>
                      <div className="text-base-content/70 text-sm">
                        {service.currency} • {service.duration_minutes} min
                      </div>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-3 mb-6 flex-grow">
                    <div className="flex items-center gap-3">
                      <FaCheckCircle className="text-primary text-sm flex-shrink-0" />
                      <span className="text-sm text-base-content/80">
                        {translate("page.services.personalized_session")}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCheckCircle className="text-primary text-sm flex-shrink-0" />
                      <span className="text-sm text-base-content/80">
                        {translate(`page.services.professional_${isPremium ? 'premium' : isBasic ? 'certified' : 'specialized'}`)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCheckCircle className="text-primary text-sm flex-shrink-0" />
                      <span className="text-sm text-base-content/80">
                        {translate("page.services.secure_payment")}
                      </span>
                    </div>
                    {isPremium && (
                      <div className="flex items-center gap-3">
                        <FaCheckCircle className="text-primary text-sm flex-shrink-0" />
                        <span className="text-sm text-base-content/80">
                          {translate("page.services.priority_attention")}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <button
                    className={`btn w-full ${isPremium
                        ? 'btn-secondary'
                        : isBasic
                          ? 'btn-primary'
                          : 'btn-outline btn-secondary'
                      }`}
                    onClick={() => {
                      console.log('🎯 SELECT_SERVICE clicked with:', service);
                      send({ type: 'SELECT_SERVICE', service });
                    }}
                  >
                    {translate(`page.services.select_${isPremium ? 'premium' : isBasic ? 'standard' : 'basic'}`)}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-base-content/60">
            {translate("page.plans.cancel_anytime")}
          </p>
        </div>
      </div>
    );
  }

  const renderPaymentMethodSelection = () => (
    <div className="p-8 max-w-3xl mx-auto">
      {/* Back Button */}
      <button
        className="btn btn-ghost flex items-center justify-center gap-2 mb-6"
        onClick={() => send({ type: 'BACK_TO_SERVICE_SELECTION' })}
      >
        <FaArrowLeft className="text-sm" />
        {translate("page.services.back_to_service")}
      </button>

      {/* Selected Service Summary */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-2 h-8 bg-primary rounded-full"></div>
          <h3 className="text-xl font-bold text-base-content">{translate("page.services.selected_service")}</h3>
        </div>

        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center justify-between mb-3">
              <h4 className="card-title text-lg">
                {current.context.service?.name}
              </h4>
              <FaCheckCircle className="text-primary text-xl" />
            </div>
            <p className="text-base-content/70 text-sm mb-4">
              {current.context.service?.description}
            </p>
            <div className="bg-secondary rounded-xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white">
                  {translate("page.services.single_payment")}
                </span>
                <span className="text-2xl font-bold text-primary">
                  ${current.context.service?.price.toLocaleString()} {current.context.service?.currency}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-2 h-8 bg-primary rounded-full"></div>
          <h3 className="text-xl font-bold text-base-content">{translate("page.services.select_payment_method")}</h3>
          <span className="badge badge-lg bg-[#1D1D1D] text-white flex items-end">Pago seguro con <img src="/img/payment-methods/epayco-white.svg" alt="epayco" className="w-12" /></span>
        </div>

        <div className="grid gap-4">
          {/* Card Payment */}
          <button
            className={`btn btn-outline w-full h-auto p-6 justify-start ${current.context.paymentMethod === 'card'
              ? 'btn-primary'
              : 'hover:btn-primary'
              }`}
            disabled={current.name === 'loading_payment'}
            onClick={() => {
              console.log('💳 SELECT_CARD_PAYMENT clicked');
              send({ type: 'SELECT_CARD_PAYMENT' });
            }}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${current.context.paymentMethod === 'card'
                  ? 'bg-primary text-primary-content'
                  : 'bg-base-200 text-base-content'
                  }`}>
                  <FaCreditCard className="text-xl" />
                </div>
                <div className="text-left flex-1">
                  <h4 className="font-semibold">{translate("page.services.card_payment")}</h4>
                  <p className="text-sm opacity-70 mb-2">{translate("page.services.card_payment_description")}</p>
                  <div className="flex items-center gap-2">
                    {PAYMENT_METHODS.filter(method => method.category === 'card').map((method: PaymentMethod) => (
                      <PaymentMethodIcon
                        key={method.id}
                        paymentMethod={method}
                      />
                    ))}
                  </div>
                </div>
              </div>
              {current.name === 'loading_payment' ? (
                <div className="flex items-center gap-2">
                  <span className="loading loading-spinner loading-sm"></span>
                  <span className="text-sm opacity-70">Procesando...</span>
                </div>
              ) : current.context.paymentMethod === 'card' ? (
                <FaCheckCircle className="text-primary text-xl" />
              ) : (
                <div className="w-6 h-6 rounded-full border-2 border-base-300"></div>
              )}
            </div>
          </button>

          {/* Other Payment */}
          <button
            className={`btn btn-outline w-full h-auto p-6 justify-start ${current.context.paymentMethod === 'other'
              ? 'btn-primary'
              : 'hover:btn-primary'
              }`}
            disabled={current.name === 'loading_payment'}
            onClick={() => {
              console.log('💰 SELECT_OTHER_PAYMENT clicked');
              send({ type: 'SELECT_OTHER_PAYMENT' });
            }}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${current.context.paymentMethod === 'other'
                  ? 'bg-primary text-primary-content'
                  : 'bg-base-200 text-base-content'
                  }`}>
                  <FaWallet className="text-xl" />
                </div>
                <div className="text-left flex-1">
                  <h4 className="font-semibold">{translate("page.services.other_payment")}</h4>
                  <p className="text-sm opacity-70 mb-2">{translate("page.services.other_payment_description")}</p>
                  <div className="flex items-center gap-1 flex-wrap">
                    {PAYMENT_METHODS.filter(method => method.category === 'other').map((method: PaymentMethod) => (
                      <PaymentMethodIcon
                        key={method.id}
                        paymentMethod={method}
                      />
                    ))}
                  </div>
                </div>
              </div>
              {current.name === 'loading_payment' ? (
                <div className="flex items-center gap-2">
                  <span className="loading loading-spinner loading-sm"></span>
                  <span className="text-sm opacity-70">Procesando...</span>
                </div>
              ) : current.context.paymentMethod === 'other' ? (
                <FaCheckCircle className="text-primary text-xl" />
              ) : (
                <div className="w-6 h-6 rounded-full border-2 border-base-300"></div>
              )}
            </div>
          </button>
        </div>
      </div>
    </div>
  )

  const renderWaitingConfirmation = () => (
    <div className="p-8 text-center">
      <FaCheckCircle className="text-success text-4xl mx-auto mb-4" />
      <h3 className="text-xl font-bold mb-2">Pago en proceso</h3>
      <p className="text-base-content/70 mb-6">Complete el pago en la ventana que se abrió</p>
      <button className="btn btn-ghost" onClick={() => send({ type: 'BACK_TO_SERVICE_SELECTION' })}>
        Volver al inicio
      </button>
    </div>
  )

  const renderContent = () => {
    switch (current.name) {
      case 'service_selection': return renderServiceSelection()
      case 'payment_method_selection': return renderPaymentMethodSelection()
      case 'waiting_confirmation': return renderWaitingConfirmation()
      default: return renderServiceSelection()
    }
  }

  return (
    <div className={className}>
      {renderContent()}
    </div>
  );
};